import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1), default 0.15
 * @param {string} options.rootMargin - Root margin for observer, default "0px"
 * @param {boolean} options.triggerOnce - Only trigger animation once, default true
 * @returns {Object} - { ref, isVisible } for attaching to elements
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

/**
 * Component wrapper for scroll animations
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child elements to animate
 * @param {string} props.animation - Animation class name (fade-up, fade-in, fade-left, fade-right, scale-in)
 * @param {number} props.delay - Animation delay in ms
 * @param {number} props.threshold - Visibility threshold
 * @param {string} props.className - Additional classes
 */
export const ScrollAnimateIn = ({ 
  children, 
  animation = 'fade-up', 
  delay = 0, 
  threshold = 0.15,
  className = '',
  as: Component = 'div'
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold });

  const animationClass = isVisible ? `scroll-${animation} scroll-animate-visible` : `scroll-${animation}`;

  return (
    <Component
      ref={ref}
      className={`scroll-animate ${animationClass} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Component>
  );
};

/**
 * Hook for staggered animations on multiple children
 * @param {number} itemCount - Number of items to animate
 * @param {number} staggerDelay - Delay between each item in ms
 * @param {Object} options - Intersection observer options
 */
export const useStaggerAnimation = (itemCount, staggerDelay = 100, options = {}) => {
  const { ref, isVisible } = useScrollAnimation(options);
  
  const getItemDelay = (index) => index * staggerDelay;
  
  return { ref, isVisible, getItemDelay };
};

export default useScrollAnimation;
